import { latestAtOf, type StudentGroup } from '@/utils/noteGroups';

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
}

interface StudentFileListProps {
  groups: StudentGroup[];
  onOpen: (group: StudentGroup) => void;
  onDeleteFile: (group: StudentGroup) => void;
}

/**
 * 학생별 오답노트 "파일(폴더)" 목록입니다.
 * 카드를 클릭하면 그 학생 파일이 열려 시험별로 정리된 오답노트를 볼 수 있습니다.
 */
export default function StudentFileList({ groups, onOpen, onDeleteFile }: StudentFileListProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {groups.map((group) => {
        const latest = latestAtOf(group);
        return (
          <div
            key={group.key}
            className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-white transition hover:border-primary-300 hover:bg-primary-50/40"
          >
            <button
              type="button"
              onClick={() => onOpen(group)}
              className="flex w-full cursor-pointer flex-col items-start gap-3 p-4 text-left md:p-5"
            >
              <div className="flex w-full items-start justify-between gap-2">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-folder-3-fill text-xl"></i>
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition group-hover:bg-primary-100 group-hover:text-primary-700">
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </span>
              </div>

              <div className="w-full min-w-0">
                <h3 className="truncate font-heading text-base font-bold text-foreground-950">{group.studentName}</h3>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-foreground-500">
                  <span>시험 {group.exams.length}개</span>
                  <span className="text-foreground-300">·</span>
                  <span>오답 {group.noteCount}개</span>
                </p>
                <p className="mt-2 flex items-center gap-1 text-[11px] text-foreground-400">
                  <i className="ri-time-line text-sm"></i>
                  최근 {formatDate(latest)}
                </p>
              </div>
            </button>

            <div className="flex items-center justify-between gap-2 border-t border-background-100 px-4 py-2.5">
              <button
                type="button"
                onClick={() => onOpen(group)}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs font-bold text-primary-700 transition hover:text-primary-800"
              >
                <i className="ri-folder-open-line text-sm"></i>
                파일 열기
              </button>
              <button
                type="button"
                onClick={() => onDeleteFile(group)}
                className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition hover:bg-accent-50 hover:text-accent-700"
                aria-label={`${group.studentName} 파일 삭제`}
              >
                <i className="ri-delete-bin-6-line text-sm"></i>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}